/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import NodeReferenceData from '../Utility/Component/Node/NodeReferenceData';
import ObservableNodeComponent from '../Utility/Component/Observable/ObservableNodeComponent';
import Effect from './Effect';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Effect for cloning an existing node and adding it to the scene. */
@cc._decorator.ccclass
export default class CloneNodeEffect extends Effect
{
    /** Reference to the node to be cloned. */
    @property({ type: NodeReferenceData, visible: true })
    private _nodeReference: NodeReferenceData = new NodeReferenceData();

    /** Parent to be used for the cloned node. */
    @property({ type: NodeReferenceData, visible: true })
    private _cloneUnderParentReference: NodeReferenceData = new NodeReferenceData();

    /** Should children of the cloned node be destroyed. */
    @property({ visible: true })
    private _destroyAllChildren: boolean = false;

    /** Size of the node pool from which the clones should be made. */
    @property({ type: cc.Integer, visible: true })
    private _nodePoolSize: number = 5;

    protected clonedNodeObservable: ObservableNodeComponent = null;

    /** @hidden */
    private _nodePool: cc.NodePool = new cc.NodePool();
    /** @hidden */
    private _clones: cc.Node[] = [];
    /** @hidden */
    private _template: cc.Node = null;
    /** @hidden */
    private _parent: cc.Node = null;
    /** @hidden */
    private _originalParent: cc.Node = null;

    protected onLoad(): void
    {
        super.onLoad();

        this.clonedNodeObservable = this.getComponentInChildren(ObservableNodeComponent);
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        this._originalParent = this._nodeReference.Value.parent;

        this._template = cc.instantiate(this._nodeReference.Value);

        if (this._destroyAllChildren)
        {
            this._template.destroyAllChildren();
            this._template.children.length = 0;
        }

        let count: number = this._nodePoolSize;

        while (count--)
        {
            this.addNodeToPool();
        }
    }

    /** Executes the clone effect. */
    protected trigger = (): void =>
    {
        let cloneableNode: cc.Node = this._nodeReference.Value;
        if (!cloneableNode) { return; }

        this._parent = this._cloneUnderParentReference.Value || cloneableNode.parent;

        // Save positions
        let relativePos: cc.Vec2 = cloneableNode.position;
        let worldPos: cc.Vec2 = this._parent.convertToWorldSpaceAR(cloneableNode.position);

        // Clone
        let clone: cc.Node = this.getNodeFromPool();

        // Attach to parent and assume position of child.
        this._parent.addChild(clone);
        clone.position = relativePos;
        clone.active = true;

        // Swap parents.
        cloneableNode.parent.removeChild(cloneableNode);
        clone.addChild(cloneableNode);

        // Grand-child, find your place in this world.
        cloneableNode.position = clone.convertToNodeSpaceAR(worldPos);

        if (this.clonedNodeObservable) { this.clonedNodeObservable.Value = clone; }

        this.complete();
    }

    /** @hidden */
    private getNodeFromPool(): cc.Node
    {
        if (this._nodePool.size() < 1)
        {
            this.addNodeToPool();
        }

        let freeNode: cc.Node = this._nodePool.get();
        this._clones.push(freeNode);

        return freeNode;
    }

    /** @hidden */
    private addNodeToPool(): void
    {
        this._nodePool.put(cc.instantiate(this._template));
    }

    /** Resets the effect's inner state to be ready to trigger again. */
    public Reset(): void
    {
        super.Reset();
        this.CleanupClones();
    }

    /** Remove any generated clones and put them back in the pool. */
    public CleanupClones(): void
    {
        let worldPos: cc.Vec2
            = this._nodeReference.Value.parent.convertToWorldSpaceAR(this._nodeReference.Value.position);

        this._nodeReference.Value.removeFromParent();
        this._originalParent.addChild(this._nodeReference.Value);
        this._nodeReference.Value.position = this._originalParent.convertToNodeSpaceAR(worldPos);

        this._clones.forEach(clone =>
        {
            clone.removeFromParent(true);
            this._nodePool.put(clone);
        }, this);

        this._clones.length = 0;
    }
}
